import { observer } from 'mobx-react-lite';
import moment from 'moment';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { DatePicker } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { momentToDate, TimeFormat } from '@feathr/hooks';

import RecommendationModal from './RecommendationModal';

interface IProps {
  campaign: TTDCampaign;
  errors: string[];
  suppressAlert?: boolean;
  recommendation: Recommendation;
}

function ExtendEndDate({
  campaign,
  errors,
  suppressAlert = false,
  recommendation,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const user = useUser();
  const timezone = user.get('timezone');

  const recommendedValue = recommendation.get('proposed_campaign_values').date_end;
  const recommendedMoment = moment.utc(recommendedValue).tz(timezone);
  const recommendedValueFormatted = recommendedMoment.format(TimeFormat.pickerShortDateTimeZone);

  const prevValue = recommendation.get('current_campaign_values').date_end;
  const prevValueFormatted = moment
    .utc(prevValue)
    .tz(timezone)
    .format(TimeFormat.pickerShortDateTimeZone);

  useEffect(() => {
    // Add recommended value to the campaign on initial load so the input shows as dirty
    handleTargetValueChange(recommendedValue);
  }, []);

  function handleTargetValueChange(newValue?: string): void {
    campaign.set({
      date_end: newValue,
    });
  }

  const alert = t('Extend end date from {{before}} to {{after}}', {
    before: prevValueFormatted,
    after: recommendedValueFormatted,
  });

  const helpText = t('Original end date: {{-date}}', { date: prevValueFormatted });

  return (
    <>
      {!suppressAlert && <RecommendationModal.Alert description={alert} />}
      <DatePicker
        autoComplete={'off'}
        dateFormat={'MMM d, yyyy h:mm aa'}
        helpPlacement={'bottom'}
        helpText={helpText}
        isFullWidth={true}
        label={t('New end date')}
        minDate={momentToDate(recommendedMoment)}
        name={'date_end'}
        onDateStrChange={handleTargetValueChange}
        showTimeSelect={true}
        timeIntervals={5}
        timezone={timezone}
        validationError={errors}
        value={campaign.get('date_end')}
      />
    </>
  );
}

export default observer(ExtendEndDate);
